import React from "react";
import ReactEcharts from "echarts-for-react";
import { getTodayWeather, getTommorowWeather, getDayAfterTommorowWeather } from "@helper/weather";
import { colors, colorsPulse, colorsSoilMoisture, colorsDendrometer } from "@helper/colors";
import RiskIndicator from "./RiskIndicator";

// --- Weather helpers ---
// PNG pictograms from MGS brand set (public/img/weather/)

const WEATHER_ICONS = {
  0: "/img/weather/sun.png",
  1: "/img/weather/partly-sunny.png",
  2: "/img/weather/cloud.png",
  3: "/img/weather/cloud.png",
};

function WeatherIcon({ status, size = 32 }) {
  const src = WEATHER_ICONS[status] || WEATHER_ICONS[2];
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      style={{ width: size, height: size, objectFit: "contain" }}
    />
  );
}

// --- Tree fallback image based on species ---

function getFallbackTreeImage(species) {
  const name = (species?.de_name || species?.name || "").toLowerCase();
  if (name.includes("amber")) return "/img/trees/amberbaum.png";
  if (name.includes("eisen")) return "/img/trees/eisenholz.png";
  if (name.includes("bluet") || name.includes("blüten")) return "/img/trees/bluetensteppe.png";
  return "/img/trees/amberbaum.png";
}

// --- Gauge helpers ---

function getGaugeConfig(tree, sensor) {
  let COLORS = colors;
  let value = 0;
  let sensorType = "pulse";
  let valueUnit = "%";
  let max = 100;
  let min = 0;
  let title = "Feuchtigkeit";

  if (sensor) {
    sensorType = sensor.pulse_sensor
      ? "pulse"
      : sensor.soil_moisture_sensor
      ? "soil_moisture"
      : sensor.soil_water_tension_sensor
      ? "soil_water_tension"
      : "dendrometer";

    COLORS =
      sensorType === "soil_moisture"
        ? colorsSoilMoisture(
            tree.soil ? tree.soil.pwp : 10,
            tree.soil ? tree.soil.max_nf : 25,
            tree.soil ? tree.soil.max_f : 45
          )
        : sensorType === "pulse"
        ? colorsPulse(tree.blue_threshold, tree.green_threshold, tree.yellow_threshold)
        : sensorType === "dendrometer"
        ? colorsDendrometer()
        : colors;

    value =
      sensorType === "pulse"
        ? sensor.pulse_sensor?.moisture_content
        : sensorType === "soil_moisture"
        ? sensor.soil_moisture_sensor?.soil_moisture
        : sensorType === "dendrometer"
        ? sensor.dendrometer_sensor?.drought_days
        : 0;

    if (sensorType === "dendrometer") {
      min = 0;
      max = 14;
      valueUnit = "Tage";
      title = "Trockentage";
    } else if (sensorType === "soil_moisture") {
      title = "Bodenfeuchte";
    }
  } else {
    value = tree?.moisture_content || 0;
  }

  return { COLORS, value, sensorType, valueUnit, max, min, title };
}

// --- Date formatting ---

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  const days = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  const months = [
    "Jan",
    "Feb",
    "Mär",
    "Apr",
    "Mai",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Okt",
    "Nov",
    "Dez",
  ];
  return `${days[d.getDay()]}, ${d.getDate()}. ${months[d.getMonth()]} ${d.getFullYear()}`;
}

// =============================================================================
// Main component: all tree info on one page, 3-column layout like the PDF
// Left: Wasserbedarf + Bild
// Center: Wetter + Wasserversorgung (Gauge + Risk)
// Right: Baum-Steckbrief + Pflege
// =============================================================================

export default function TreeFullPage({ tree, weather }) {
  const species = tree?.species;
  const imageUrl = tree?.images?.[0]?.image || tree?.image || getFallbackTreeImage(species);
  const installations = tree?.installations || [];
  const sensor = installations[0]?.sensor || null;
  const { COLORS, value, sensorType, valueUnit, max, min, title } = getGaugeConfig(tree, sensor);

  // 3D model viewer URL (replaces gauge when available)
  const modelUrl = tree?.three_d_model_url?.trim() || null;
  const viewerUrl = modelUrl
    ? `/darbots-viewer/viewer.html?model=${encodeURIComponent(modelUrl)}`
    : null;

  const displayValue =
    sensorType === "dendrometer"
      ? Math.max(0, Math.min(14, Number(value)))
      : typeof value === "number"
      ? value.toFixed(0)
      : value;

  const maintenances = tree?.maintenances || [];
  const now = new Date();
  const pastEntries = [...maintenances]
    .filter((m) => new Date(m.date) <= now)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  const futureEntries = [...maintenances]
    .filter((m) => new Date(m.date) > now)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const lastCare = pastEntries[0];
  const nextCare = futureEntries[0];

  const gaugeOption = {
    series: [
      {
        type: "gauge",
        min,
        max,
        splitNumber: sensorType === "dendrometer" ? 14 : undefined,
        radius: "90%",
        center: ["50%", "58%"],
        axisLine: { lineStyle: { width: 22, color: COLORS } },
        splitLine: { show: false },
        axisTick: { show: false },
        axisLabel: { fontSize: 12, distance: 10, color: "#3D5A6B" },
        pointer: { length: "55%", width: 5 },
        detail: {
          show: true,
          fontSize: 28,
          fontWeight: 700,
          color: "#0E1D2C",
          offsetCenter: [0, "75%"],
          formatter: (val) => `${typeof val === "number" ? Math.round(val) : val} ${valueUnit}`,
        },
        title: {
          show: true,
          fontSize: 14,
          fontWeight: 600,
          color: "#3D5A6B",
          offsetCenter: [0, "92%"],
        },
        data: [{ value: displayValue, name: title }],
      },
    ],
  };

  return (
    <div className="tree-full-page">
      {/* ====== LEFT COLUMN: Wasserbedarf ====== */}
      <div className="tfp-col tfp-col--left">
        <div className="tfp-section-title">Wasserbedarf</div>

        <img src={imageUrl} alt={tree?.name || "Baum"} className="tfp-tree-image" />

        <div className="tfp-water-box">
          <img
            src="/img/weather/watering.png"
            alt=""
            aria-hidden="true"
            className="tfp-water-icon-img"
          />
          <div>
            <div className="tfp-water-amount">
              {species?.water_requirement_per_day
                ? `${species.water_requirement_per_day} Liter`
                : "50 bis 100 Liter"}
            </div>
            <div className="tfp-water-label">pro Gießgang</div>
          </div>
        </div>
      </div>

      {/* ====== CENTER COLUMN: Wetter + Wasserversorgung ====== */}
      <div className="tfp-col tfp-col--center">
        {/* Weather – title + inline forecast */}
        <div className="tfp-weather-header">
          <div className="tfp-section-title tfp-section-title--inline">Wetter</div>
          {weather ? (
            <div className="tfp-weather-inline">
              {[
                {
                  label: "Heute",
                  temp: weather.temperature?.today,
                  status: getTodayWeather(weather),
                },
                {
                  label: "Morgen",
                  temp: weather.temperature?.tomorrow,
                  status: getTommorowWeather(weather),
                },
                {
                  label: "Überm.",
                  temp: weather.temperature?.day_after_tomorrow,
                  status: getDayAfterTommorowWeather(weather),
                },
              ].map((d, i) => (
                <div key={i} className="tfp-weather-badge">
                  <span className="tfp-weather-badge__label">{d.label}</span>
                  <WeatherIcon status={d.status} size={28} />
                  <span className="tfp-weather-badge__temp">
                    {d.temp != null ? `${d.temp}°` : "—"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="tfp-weather-inline tfp-empty-text">Keine Wetterdaten</div>
          )}
        </div>

        {/* Water supply: 3D viewer OR Gauge */}
        <div className="tfp-section-title">Wasserversorgung</div>
        {viewerUrl ? (
          <div className="tfp-3d-container">
            <iframe
              title="3D Baummodell"
              src={viewerUrl}
              className="tfp-3d-iframe"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="tfp-gauge-container">
            <ReactEcharts
              option={gaugeOption}
              style={{ height: "280px", width: "100%" }}
              notMerge
            />
          </div>
        )}

        <RiskIndicator healthState={tree?.health_state} />
      </div>

      {/* ====== RIGHT COLUMN: Steckbrief + Pflege ====== */}
      <div className="tfp-col tfp-col--right">
        <div className="tfp-section-title">Baum-Steckbrief</div>

        <div className="tfp-profile-card">
          {tree?.name && (
            <div className="tfp-stat">
              <span className="tfp-stat-label">Name</span>
              <span className="tfp-stat-value">{tree.name}</span>
            </div>
          )}
          {(species?.de_name || species?.name) && (
            <div className="tfp-stat">
              <span className="tfp-stat-label">Art</span>
              <span className="tfp-stat-value">{species.de_name || species.name}</span>
            </div>
          )}
          {species?.height && (
            <div className="tfp-stat">
              <span className="tfp-stat-label">Höhe</span>
              <span className="tfp-stat-value">{species.height}</span>
            </div>
          )}
          {species?.crown_width && (
            <div className="tfp-stat">
              <span className="tfp-stat-label">Breite</span>
              <span className="tfp-stat-value">{species.crown_width}</span>
            </div>
          )}
          {species?.root_type && (
            <div className="tfp-stat">
              <span className="tfp-stat-label">Wurzel</span>
              <span className="tfp-stat-value">{species.root_type}</span>
            </div>
          )}
        </div>

        {/* Pflege */}
        <div className="tfp-section-title" style={{ marginTop: "16px" }}>
          Pflege
        </div>
        <div className="tfp-care-card">
          {nextCare ? (
            <div className="tfp-care-item">
              <span className="tfp-care-badge tfp-care-badge--next">Nächster Termin</span>
              <span className="tfp-care-date">{formatDate(nextCare.date)}</span>
            </div>
          ) : null}
          {lastCare ? (
            <div className="tfp-care-item">
              <span className="tfp-care-badge tfp-care-badge--last">Letzte Pflege</span>
              <span className="tfp-care-date">{formatDate(lastCare.date)}</span>
            </div>
          ) : null}
          {!nextCare && !lastCare && <div className="tfp-empty-text">Keine Pflegetermine</div>}
        </div>

        {/* Sensors */}
        {installations.length > 0 && (
          <>
            <div className="tfp-section-title" style={{ marginTop: "16px" }}>
              Baumsensoren
            </div>
            <div className="tfp-sensors">
              {installations.slice(0, 3).map((inst, i) => (
                <div key={inst.id || i} className="tfp-sensor-item">
                  <span className="tfp-sensor-dot" />
                  <span>{inst.sensor?.name || inst.name || `Sensor ${i + 1}`}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
